import { ageOn } from './accounts.js';
import { COURSE_FIELDS, type CourseField } from './courses.js';
import { fail } from './errors.js';

/*
 * 선생님 찾기: 센터에 다니지 않는 보호자도 공개된 선생님을 보고 수업을 신청한다.
 * - 선생님이 소개를 쓰고 공개를 요청하면, 소속 센터 원장이 승인해야 목록에 나온다.
 * - 센터가 없는 개인 선생님은 본사가 자격 정보를 확인하고 승인한다.
 * - 신청 단계에서 선생님은 아이의 만 나이와 전할 말만 본다. 수락한 뒤에만 보호자 연락처와 아이 이름을 본다.
 * - 수업이 끝나거나 신청이 닫히면 연락처를 지운다.
 * - 결제는 없다. SelfOn 은 연결만 하고, 수업 방법과 비용은 보호자와 선생님이 직접 정한다.
 */

export const TEACHER_MODES = ['VISIT', 'CENTER', 'ONLINE'] as const;
export type TeacherMode = (typeof TEACHER_MODES)[number];
export const TEACHER_MODE_LABEL: Record<TeacherMode, string> = { VISIT: '방문 수업', CENTER: '센터 수업', ONLINE: '온라인 수업' };

export const TEACHER_LIMITS = { title: 30, region: 40, intro: 500, credential: 200, note: 300, reason: 200, feeMax: 1_000_000 };
/** 자녀 한 명이 동시에 답을 기다릴 수 있는 신청 수 */
export const TEACHER_PENDING_LIMIT = 3;

/** DRAFT 비공개 · PENDING 원장 승인 대기 · LISTED 목록에 공개 */
export type TeacherStatus = 'DRAFT' | 'PENDING' | 'LISTED';
export const TEACHER_STATUS_LABEL: Record<TeacherStatus, string> = { DRAFT: '비공개', PENDING: '원장님 승인 대기', LISTED: '공개 중' };

export interface TeacherProfile {
  userId: string;
  centerId?: string;   // 없으면 개인 선생님
  name: string;
  title: string;        // 선생님이 직접 적은 직함. SelfOn 이 자격을 확인한 값이 아니다.
  fields: CourseField[];
  modes: TeacherMode[];
  region: string;       // 수업 가능 지역
  intro: string;
  fee?: number;         // 회당 수업료(원, 없으면 상담 후 안내)
  credential?: string;  // 개인 선생님의 자격 정보. 본사 검토용이라 보호자에게 보내지 않는다.
  status: TeacherStatus;
  version: number;
  updatedAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
}

export type TeacherInput = Pick<TeacherProfile, 'title' | 'fields' | 'modes' | 'region' | 'intro' | 'fee' | 'credential'>;

const text = (v: unknown, max: number, label: string, required = false) => {
  const s = typeof v === 'string' ? v.trim() : '';
  if (required && !s) fail('VALIDATION', `${label}을(를) 입력하세요.`);
  if (s.length > max) fail('VALIDATION', `${label}은(는) ${max}자 이하로 입력하세요.`);
  return s;
};
const picks = <T extends string>(v: unknown, allowed: readonly T[], label: string) => {
  const list = Array.isArray(v) ? [...new Set(v)] : [];
  if (!list.length || !list.every((x) => allowed.includes(x as T))) fail('VALIDATION', `${label}을(를) 하나 이상 고르세요.`);
  return list as T[];
};

/** 저장. publish 면 원장 승인을 기다리고, 아니면 비공개가 된다. 공개 중인 소개를 고치면 다시 승인을 받는다. */
export function saveTeacherProfile(
  cur: TeacherProfile | undefined, i: { userId: string; centerId?: string; name: string; input: Partial<TeacherInput>; publish: boolean; now: string },
): TeacherProfile {
  const v = i.input;
  const fee = v.fee === undefined ? undefined : Number(v.fee);
  if (fee !== undefined && (!Number.isInteger(fee) || fee < 0 || fee > TEACHER_LIMITS.feeMax)) fail('VALIDATION', '회당 수업료를 확인하세요.');
  const credential = text(v.credential, TEACHER_LIMITS.credential, '자격 정보', !i.centerId) || undefined;
  return {
    userId: i.userId, ...(i.centerId ? { centerId: i.centerId } : {}), name: i.name,
    title: text(v.title, TEACHER_LIMITS.title, '직함', true),
    fields: picks(v.fields, COURSE_FIELDS, '분야'), modes: picks(v.modes, TEACHER_MODES, '수업 방식'),
    region: text(v.region, TEACHER_LIMITS.region, '수업 지역', true),
    intro: text(v.intro, TEACHER_LIMITS.intro, '소개', true),
    ...(fee === undefined ? {} : { fee }), ...(credential ? { credential } : {}),
    status: i.publish ? 'PENDING' : 'DRAFT', version: (cur?.version ?? 0) + 1, updatedAt: i.now,
  };
}

/** 원장·본사 검토: 승인하면 공개, 아니면 비공개로 돌린다. 공개 중인 선생님도 내릴 수 있다. */
export function reviewTeacherProfile(cur: TeacherProfile, approve: boolean, by: string, now: string): TeacherProfile {
  if (approve && cur.status !== 'PENDING') fail('CONFLICT', '승인을 기다리는 소개가 아닙니다.');
  return { ...cur, status: approve ? 'LISTED' : 'DRAFT', version: cur.version + 1, updatedAt: now, reviewedBy: by, reviewedAt: now };
}

/** 보호자에게 보낼 때: 공개된 선생님만, 내부 기록 제외 */
export function publicTeachers(list: readonly TeacherProfile[]) {
  return list.filter((t) => t.status === 'LISTED').map(({ reviewedBy: _r, reviewedAt: _a, credential: _c, ...rest }) => rest);
}

/* ---------------- 수업 신청 ---------------- */

/** 희망 시간: 요일과 시작 시각(1시간 단위)의 칸. 저장 값은 'MON_09' 형식이다. */
export const TEACHER_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;
export type TeacherDay = (typeof TEACHER_DAYS)[number];
export const TEACHER_DAY_LABEL: Record<TeacherDay, string> = { MON: '월', TUE: '화', WED: '수', THU: '목', FRI: '금', SAT: '토', SUN: '일' };
/** 고를 수 있는 시작 시각: 오전 9시부터 저녁 8시(밤 9시에 끝남)까지 */
export const TEACHER_HOURS = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20] as const;
export const wishKey = (day: TeacherDay, hour: number) => `${day}_${String(hour).padStart(2, '0')}`;
const WISH_ORDER: readonly string[] = TEACHER_DAYS.flatMap((d) => TEACHER_HOURS.map((h) => wishKey(d, h)));

/** 중복을 없애고 월요일 오전부터 차례로 정리한다. */
export function cleanWishTimes(list: readonly string[]): string[] {
  if (!list.length) fail('VALIDATION', '희망 요일과 시간을 하나 이상 고르세요.');
  if (!list.every((w) => WISH_ORDER.includes(w))) fail('VALIDATION', '희망 요일과 시간을 확인하세요.');
  return WISH_ORDER.filter((w) => list.includes(w));
}

/** 화면 표시: 이어진 시간은 묶는다. '월 9~11시, 14~15시, 수 19~20시' */
export function wishTimesText(list: readonly string[]): string {
  return TEACHER_DAYS.flatMap((d) => {
    const ranges: Array<[number, number]> = [];
    for (const h of TEACHER_HOURS.filter((x) => list.includes(wishKey(d, x)))) {
      const last = ranges[ranges.length - 1];
      if (last && last[1] === h) last[1] = h + 1;
      else ranges.push([h, h + 1]);
    }
    return ranges.length ? [`${TEACHER_DAY_LABEL[d]} ${ranges.map(([from, to]) => `${from}~${to}시`).join(', ')}`] : [];
  }).join(', ');
}

/** PENDING 답 기다림 · ACCEPTED 수락 · DECLINED 거절 · CANCELLED 보호자 취소 · ENDED 종료 */
export type TeacherRequestStatus = 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'CANCELLED' | 'ENDED';
export const TEACHER_REQUEST_LABEL: Record<TeacherRequestStatus, string> = { PENDING: '답변 기다리는 중', ACCEPTED: '수락됨', DECLINED: '거절됨', CANCELLED: '취소함', ENDED: '종료' };

export interface TeacherRequest {
  id: string;
  teacherId: string;
  teacherName: string;
  centerId?: string;
  childId: string;
  guardianId: string;
  childAge: number;     // 신청 때의 만 나이
  wishTimes: string[];  // 희망 요일·시간
  note?: string;
  /** 수락 뒤에만 선생님에게 보인다. 신청이 닫히면 지운다. */
  contact?: { guardianName: string; childName: string; phone: string };
  status: TeacherRequestStatus;
  reason?: string;      // 거절 사유(보호자에게 보인다)
  guardianUnread?: boolean; // 선생님이 답했고 보호자가 아직 보지 않았다
  createdAt: string;
  decidedAt?: string;
  version: number;
}

const active = (r: TeacherRequest) => r.status === 'PENDING' || r.status === 'ACCEPTED';
const closed = (r: TeacherRequest, status: TeacherRequestStatus, now: string): TeacherRequest => {
  const { contact: _gone, ...rest } = r;
  return { ...rest, status, decidedAt: now, version: r.version + 1 };
};

const PHONE_RE = /^01[016789]-?\d{3,4}-?\d{4}$/;

export function newTeacherRequest(
  i: {
    id: string; teacher: TeacherProfile; child: { id: string; name: string; birthDate: string }; guardianId: string; guardianName: string;
    phone: string; contactConsent: boolean; wishTimes: readonly string[]; note?: string; now: string; todayKst: string;
  },
  existing: readonly TeacherRequest[],
): TeacherRequest {
  if (i.teacher.status !== 'LISTED') fail('NOT_FOUND', '선생님을 찾을 수 없습니다.');
  if (!i.contactConsent) fail('VALIDATION', '선생님이 수락하면 연락처를 전달하는 데 동의해 주세요.');
  const wishTimes = cleanWishTimes(i.wishTimes);
  const phone = i.phone.trim();
  if (!PHONE_RE.test(phone)) fail('VALIDATION', '휴대폰 번호 형식을 확인하세요(예: 010-1234-5678).');
  const mine = existing.filter((r) => r.childId === i.child.id);
  if (mine.some((r) => r.teacherId === i.teacher.userId && active(r))) fail('CONFLICT', '이미 이 선생님께 신청했습니다.');
  if (mine.filter((r) => r.status === 'PENDING').length >= TEACHER_PENDING_LIMIT) fail('CONFLICT', `답을 기다리는 신청은 자녀당 ${TEACHER_PENDING_LIMIT}개까지입니다.`);
  const note = text(i.note, TEACHER_LIMITS.note, '선생님께 전할 말') || undefined;
  return {
    id: i.id, teacherId: i.teacher.userId, teacherName: i.teacher.name, centerId: i.teacher.centerId, childId: i.child.id, guardianId: i.guardianId,
    childAge: ageOn(i.child.birthDate, i.todayKst), wishTimes, note, contact: { guardianName: i.guardianName, childName: i.child.name, phone },
    status: 'PENDING', createdAt: i.now, version: 1,
  };
}

/** 보호자 취소: 수락 전후 모두 가능. 닫으면 연락처가 지워진다. */
export function cancelTeacherRequest(r: TeacherRequest, guardianId: string, now: string): TeacherRequest {
  if (r.guardianId !== guardianId) fail('NOT_FOUND', '신청을 찾을 수 없습니다.');
  if (!active(r)) fail('CONFLICT', '이미 처리된 신청입니다.');
  return closed(r, 'CANCELLED', now);
}

export function decideTeacherRequest(r: TeacherRequest, teacherId: string, accept: boolean, now: string, reason?: string): TeacherRequest {
  if (r.teacherId !== teacherId) fail('NOT_FOUND', '신청을 찾을 수 없습니다.');
  if (r.status !== 'PENDING') fail('CONFLICT', '이미 처리된 신청입니다.');
  if (accept) return { ...r, status: 'ACCEPTED', guardianUnread: true, decidedAt: now, version: r.version + 1 };
  const why = text(reason, TEACHER_LIMITS.reason, '거절 사유') || undefined;
  return { ...closed(r, 'DECLINED', now), guardianUnread: true, ...(why ? { reason: why } : {}) };
}

/** 선생님이 수업을 마쳤을 때. 연락처를 지운다. */
export function endTeacherRequest(r: TeacherRequest, teacherId: string, now: string): TeacherRequest {
  if (r.teacherId !== teacherId) fail('NOT_FOUND', '신청을 찾을 수 없습니다.');
  if (r.status !== 'ACCEPTED') fail('CONFLICT', '수락한 신청만 종료할 수 있습니다.');
  return { ...closed(r, 'ENDED', now), guardianUnread: true };
}

/** 보호자가 내 신청을 봤을 때. 알림만 지우므로 버전은 그대로 둔다. */
export function seenTeacherRequest(r: TeacherRequest): TeacherRequest {
  const { guardianUnread: _seen, ...rest } = r;
  return rest;
}

/** 선생님에게 보낼 때: 수락 전에는 연락처를 빼고 보낸다. */
export function teacherView(r: TeacherRequest) {
  const { contact, guardianId: _g, childId: _c, guardianUnread: _u, ...rest } = r;
  return r.status === 'ACCEPTED' ? { ...rest, contact } : rest;
}
